import axios from 'axios';
import { OP_RESULT_CODES, OpResult } from '@sdflc/api-helpers';
import { UploadedFile } from './UploadedFileService';

/** Result of scanning a receipt image */
export interface ReceiptScanResult {
  /** Whether the image was successfully recognized as a receipt/document */
  success: boolean;
  /** Confidence level of the extraction */
  confidence: 'high' | 'medium' | 'low';
  /** Human-readable description of what was detected */
  description: string;
  /** If not successful, reason why (blurry, not a receipt, etc.) */
  failureReason: string | null;
  /**
   * Record type: 1=Refuel, 2=Expense.
   * Claude determines this from the document content.
   */
  expenseType: 1 | 2;
  /**
   * Pre-filled fields that map directly to ExpenseInput.
   * The frontend can spread these into the form state.
   * Only fields that were extracted from the receipt are included.
   */
  fields: ReceiptScanFields;
  /**
   * Raw extracted details that don't map to ExpenseInput but may be useful
   * for display or logging purposes (e.g. line items, card info, pump number).
   */
  extra: ReceiptScanExtra;

  /**
   * Files uploaded to ms_storage after a successful scan.
   * Contains the receipt image(s) stored for later attachment to the expense record.
   * Only populated when the scan is successful and upload succeeds.
   */
  uploadedFiles?: UploadedFile[];

  /**
   * Location coordinates
   */
  _geocoded: any;
}

/** Fields that map directly to ExpenseInput */
export interface ReceiptScanFields {
  // -- Common fields --
  odometer?: number;
  whenDone?: string;           // ISO 8601 datetime: "YYYY-MM-DDTHH:mm:ss"
  location?: string;           // Vendor/business name
  whereDone?: string;          // Human-readable location description
  subtotal?: number;
  tax?: number;
  fees?: number;
  totalPrice?: number;
  paidInCurrency?: string;     // ISO 4217: CAD, USD, EUR, RUB, etc.
  comments?: string;

  // -- Address fields --

  address1?: string;
  address2?: string;
  city?: string;
  postalCode?: string;
  stateProvince?: string;
  country?: string;
  countryId?: string;          // 2-letter code: CA, US, FR, RU, etc.

  // -- Expense-specific (expenseType=2) --
  kindId?: number;
  costWork?: number;
  costParts?: number;
  shortNote?: string;

  // -- Refuel-specific (expenseType=1) --
  refuelVolume?: number;
  volumeEnteredIn?: string;    // "L" or "gal"
  pricePerVolume?: number;
  fuelGrade?: string;
}

/** Extra extracted data that doesn't map to ExpenseInput */
export interface ReceiptScanExtra {
  /** Payment method: cash, credit, debit, tap, mobile */
  paymentMethod?: string;
  /** Last 4 digits of payment card */
  cardLastFour?: string;
  /** Receipt or transaction number */
  receiptNumber?: string;
  /** Odometer unit as printed (km or mi) — helps frontend convert if needed */
  odometerUnit?: 'km' | 'mi';
  /** Pump number (fuel receipts) */
  pumpNumber?: string;
  /** License plate if printed on receipt */
  licensePlate?: string;
  /** Vehicle info if printed (make, model, year, VIN) */
  vehicleInfo?: string;
  /** Technician or server name */
  staffName?: string;
  /** Work order or invoice number */
  workOrderNumber?: string;
  /** Individual line items from the receipt */
  lineItems?: {
    description: string;
    quantity?: number;
    unitPrice?: number;
    amount?: number;
    type?: 'labor' | 'part' | 'food' | 'other';
  }[];
  /** Tip amount (food receipts) */
  tipAmount?: number;
  /** Number of guests (food receipts) */
  guestCount?: number;
  /** Parking duration */
  parkingDuration?: string;
  /** Parking entry datetime */
  parkingEntryDateTime?: string;
  /** Parking exit datetime */
  parkingExitDateTime?: string;
  /** Parking spot or zone */
  parkingSpotOrZone?: string;
  /** Fine/ticket number */
  ticketNumber?: string;
  /** Fine due date */
  fineDueDate?: string;
  /** Violation type */
  violationType?: string;
  /** Violation location */
  violationLocation?: string;
  /** Insurance policy number */
  policyNumber?: string;
  /** Insurance coverage period start */
  coveragePeriodStart?: string;
  /** Insurance coverage period end */
  coveragePeriodEnd?: string;
  /** Toll road/highway name */
  tollRoadName?: string;
  /** Toll entry point */
  tollEntryPoint?: string;
  /** Toll exit point */
  tollExitPoint?: string;
}

/**
 * Scanner service that handles receipt scanning API calls.
 */
export class ScannerService {
  /**
   * Uploads a receipt image to the backend for scanning via Claude Vision API.
   * Returns a ReceiptScanResult with pre-filled ExpenseInput fields.
   *
   * @param file - The image file (JPEG, PNG, GIF, or WebP) to scan
   * @returns OpResult containing ReceiptScanResult on success
   */
  static async scanReceipt(file: File): Promise<OpResult> {
    try {
      const formData = new FormData();

      formData.append('receipts', file);
      formData.append('currentTimeUtc', new Date().toISOString());
      formData.append('currentTimeZoneOffset', String(new Date().getTimezoneOffset()));

      const response = await axios.post(
        `${import.meta.env.VITE_ROOT_API_URL}/api/scanner/scan-receipt`,
        formData,
        {
          headers: {
            'x-space-id': import.meta.env.VITE_SPACE_ID,
          },
          withCredentials: true,
          timeout: 120000, // 2 minutes — image analysis can take time
        }
      );

      const result = new OpResult();
      const responseData = response.data;

      if (responseData?.errors?.length > 0) {
        for (const error of responseData.errors) {
          result.addError(error.name || '', error.errors || error.message || 'Unknown error', OP_RESULT_CODES.FAILED);
        }
        return result;
      }

      result.setData(responseData?.data || responseData);
      return result;
    } catch (error: any) {
      const result = new OpResult();

      if (axios.isAxiosError(error)) {
        const status = error.response?.status;
        const serverErrors = error.response?.data?.errors;

        if (serverErrors?.length > 0) {
          for (const err of serverErrors) {
            result.addError(err.name || '', err.errors || err.message || 'Server error', OP_RESULT_CODES.FAILED);
          }
          return result;
        }

        if (status === 401) {
          result.addError('', 'Authentication required. Please log in again.', OP_RESULT_CODES.UNAUTHORIZED);
        } else if (status === 413) {
          result.addError('', 'Image file is too large. Please use a smaller image.', OP_RESULT_CODES.VALIDATION_FAILED);
        } else if (error.code === 'ECONNABORTED') {
          result.addError('', 'Receipt scanning timed out. Please try again.', OP_RESULT_CODES.FAILED);
        } else {
          result.addError('', error.response?.data?.message || 'Failed to scan receipt. Please try again.', OP_RESULT_CODES.FAILED);
        }
      } else {
        result.addError('', `Failed to scan receipt: ${error.message}`, OP_RESULT_CODES.FAILED);
      }

      return result;
    }
  }
}